"use client";
import { PieChart, Pie, ResponsiveContainer, Cell, Customized } from "recharts";
import {
  GameAlignmentType,
  LineType,
  Odd,
  Odd2,
  OddVariant,
  ScoreboardGame,
  ScoreboardGameBasic,
  TeamBasic,
  TeamColor,
  buildOdd,
  formatOddVariantShort,
} from "common";
import SideBySideTeamLogo from "../team/side-by-side-logo";
import { calculatePercentage } from "@/utils";
import Tabs from "../primitives/tabs";

interface CustomLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  align: "start" | "end";
  team: string;
  color: string;
  legendColor: string;
  picks: number;
  value: number;
  temperature: "hot" | "cold" | "none";
  dark?: boolean;
  selectedOdd: OddVariant;
}
const renderCustomLabel = (props: CustomLabelProps) => {
  const labelDistance = 42;
  let x, y;
  let iconX;
  // y = props.cy;
  x = 50;
  y = 50;
  if (props.align === "start") {
    x = props.cx - props.outerRadius - labelDistance;
    iconX = x - 12 - 24;
  } else if (props.align === "end") {
    x = props.cx + props.outerRadius + labelDistance;
    iconX = x + 12 + 12;
  } else {
    return null;
  }
  // const iconX = props.cx - 16 / 2;
  // const svgWidth = 12;
  // const svgX = x - svgWidth - 10;
  return (
    <>
      {props.temperature === "hot" ? (
        <svg
          x={iconX}
          y={y - 14}
          width="12"
          height="14"
          viewBox="0 0 12 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.988 7.37373C9.9413 4.65373 6.21464 4.50706 7.11464 0.553726C7.1813 0.260393 6.86797 0.0337264 6.61464 0.18706C4.19464 1.61373 2.45464 4.47373 3.91464 8.22039C4.03464 8.52706 3.67464 8.81373 3.41464 8.61373C2.20797 7.70039 2.0813 6.38706 2.18797 5.44706C2.22797 5.10039 1.77464 4.93373 1.5813 5.22039C1.12797 5.91373 0.667969 7.03373 0.667969 8.72039C0.921302 12.4537 4.07464 13.6004 5.20797 13.7471C6.82797 13.9537 8.5813 13.6537 9.8413 12.5004C11.228 11.2137 11.7346 9.16039 10.988 7.37373ZM4.8013 10.7271C5.7613 10.4937 6.25464 9.80039 6.38797 9.18706C6.60797 8.23373 5.74797 7.30039 6.32797 5.79373C6.54797 7.04039 8.50797 7.82039 8.50797 9.18039C8.5613 10.8671 6.73464 12.3137 4.8013 10.7271Z"
            fill="url(#paint0_linear_12131_64809)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_12131_64809"
              x1="6.00007"
              y1="0.137695"
              x2="6.00007"
              y2="13.8076"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#FF0016" />
              <stop offset="1" stop-color="#FF9900" />
            </linearGradient>
          </defs>
        </svg>
      ) : props.temperature === "cold" ? (
        <svg
          x={iconX}
          y={y - 14}
          width="12"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_12131_64842)">
            <path
              d="M15.1977 9.58362C15.1769 9.50758 15.1413 9.4364 15.0929 9.37414C15.0446 9.31188 14.9844 9.25976 14.9159 9.22077C14.8474 9.18177 14.7719 9.15667 14.6936 9.14689C14.6154 9.13712 14.536 9.14286 14.46 9.16379L12.3189 9.76354C12.3013 9.7725 12.2817 9.77717 12.2619 9.77717C12.2421 9.77717 12.2226 9.7725 12.205 9.76354L11.6532 9.44567C11.6186 9.42597 11.5928 9.39379 11.5812 9.35571C11.5661 9.33152 11.5597 9.30292 11.563 9.2746C11.5662 9.24627 11.579 9.21989 11.5992 9.19978L12.133 8.30614C12.1856 8.21497 12.2133 8.11155 12.2133 8.00627C12.2133 7.90099 12.1856 7.79756 12.133 7.70639L11.5992 6.80076C11.5903 6.78311 11.5856 6.76359 11.5856 6.74379C11.5856 6.72399 11.5903 6.70447 11.5992 6.68681C11.6108 6.64874 11.6366 6.61655 11.6712 6.59685L12.199 6.27898C12.2166 6.27002 12.2361 6.26535 12.2559 6.26535C12.2757 6.26535 12.2953 6.27002 12.3129 6.27898L14.454 6.87873C14.5056 6.88746 14.5583 6.88746 14.61 6.87873C14.769 6.89941 14.9298 6.85605 15.0569 6.7582C15.184 6.66035 15.267 6.51601 15.2877 6.35695C15.3083 6.19788 15.265 6.03712 15.1671 5.91002C15.0693 5.78292 14.925 5.6999 14.7659 5.67923L13.0446 5.21742C13.0064 5.20628 12.9742 5.18044 12.9551 5.14557C12.936 5.1107 12.9315 5.06967 12.9427 5.03149L13.3985 3.29821C13.4337 3.14676 13.4088 2.98753 13.329 2.8541C13.2491 2.72067 13.1205 2.62348 12.9704 2.58302C12.8202 2.54257 12.6602 2.56201 12.5242 2.63726C12.3881 2.7125 12.2865 2.83766 12.2409 2.98634L11.6412 5.14545C11.6296 5.18352 11.6038 5.21571 11.5692 5.23541L10.9995 5.60126C10.9649 5.61944 10.9248 5.62398 10.887 5.61398C10.8492 5.60399 10.8166 5.58018 10.7955 5.54728L10.3038 4.72562C10.2555 4.63222 10.1836 4.55314 10.0952 4.4963C10.0068 4.43946 9.90495 4.40685 9.79996 4.40175H8.7504C8.71064 4.40175 8.6725 4.38596 8.64438 4.35784C8.61626 4.32972 8.60046 4.29158 8.60046 4.25181V3.51412C8.60171 3.47439 8.61653 3.43629 8.64245 3.40616L10.2258 1.82881C10.2817 1.77289 10.3261 1.70651 10.3563 1.63344C10.3866 1.56038 10.4022 1.48207 10.4022 1.40299C10.4022 1.32391 10.3866 1.2456 10.3563 1.17253C10.3261 1.09947 10.2817 1.03308 10.2258 0.977164C10.1699 0.921243 10.1035 0.876885 10.0304 0.846622C9.95735 0.816358 9.87905 0.800781 9.79996 0.800781C9.72088 0.800781 9.64257 0.816358 9.56951 0.846622C9.49645 0.876885 9.43006 0.921243 9.37414 0.977164L8.10867 2.24864C8.09468 2.26315 8.07791 2.27469 8.05937 2.28258C8.04082 2.29046 8.02087 2.29452 8.00071 2.29452C7.98056 2.29452 7.96061 2.29046 7.94206 2.28258C7.92351 2.27469 7.90675 2.26315 7.89276 2.24864L6.62729 0.977164C6.51435 0.864228 6.36118 0.800781 6.20147 0.800781C6.04175 0.800781 5.88858 0.864228 5.77564 0.977164C5.66271 1.0901 5.59926 1.24327 5.59926 1.40299C5.59926 1.5627 5.66271 1.71588 5.77564 1.82881L7.35898 3.40616C7.3849 3.43629 7.39972 3.47439 7.40096 3.51412V4.25181C7.40096 4.29158 7.38517 4.32972 7.35705 4.35784C7.32893 4.38596 7.29079 4.40175 7.25103 4.40175H6.20147C6.09738 4.40177 5.9951 4.42888 5.90466 4.4804C5.81423 4.53193 5.73876 4.6061 5.68568 4.69563L5.19389 5.51729C5.17803 5.55262 5.1496 5.58078 5.11413 5.5963C5.07865 5.61182 5.03867 5.61359 5.00197 5.60126L4.40222 5.23541C4.36762 5.21571 4.34187 5.18352 4.33025 5.14545L3.7305 2.98634C3.66616 2.86647 3.56299 2.77204 3.43791 2.71854C3.31283 2.66504 3.17328 2.65565 3.04217 2.69192C2.91105 2.72818 2.79616 2.80795 2.71635 2.91812C2.63655 3.02829 2.59656 3.16232 2.60297 3.29821L3.06478 5.03149C3.07591 5.06967 3.07142 5.1107 3.0523 5.14557C3.03318 5.18044 3.00099 5.20628 2.96282 5.21742L1.24753 5.67923C1.08847 5.6999 0.944137 5.78292 0.846283 5.91002C0.74843 6.03712 0.705074 6.19788 0.725752 6.35695C0.74643 6.51601 0.829449 6.66035 0.956546 6.7582C1.08364 6.85605 1.24441 6.89941 1.40347 6.87873C1.45508 6.88746 1.50779 6.88746 1.5594 6.87873L3.70051 6.27898C3.71817 6.27002 3.73769 6.26535 3.75749 6.26535C3.77729 6.26535 3.79681 6.27002 3.81446 6.27898L4.36623 6.59685C4.40083 6.61655 4.42658 6.64874 4.4382 6.68681C4.44211 6.7073 4.44087 6.72844 4.43458 6.74833C4.4283 6.76822 4.41718 6.78623 4.40222 6.80076L3.86844 7.6944C3.8158 7.78557 3.78809 7.88899 3.78809 7.99427C3.78809 8.09955 3.8158 8.20297 3.86844 8.29415L4.40222 9.19978C4.41117 9.21743 4.41584 9.23695 4.41584 9.25675C4.41584 9.27655 4.41117 9.29607 4.40222 9.31373C4.39059 9.3518 4.36484 9.38399 4.33025 9.40369L3.80247 9.72156C3.78481 9.73052 3.76529 9.73518 3.74549 9.73518C3.72569 9.73518 3.70617 9.73052 3.68851 9.72156L1.54741 9.12181C1.4714 9.10088 1.39202 9.09514 1.3138 9.10491C1.23557 9.11469 1.16004 9.13979 1.09153 9.17878C1.02301 9.21778 0.962855 9.26989 0.914499 9.33215C0.866144 9.39442 0.830538 9.4656 0.809717 9.54163C0.78889 9.61804 0.783399 9.69781 0.793562 9.77634C0.803725 9.85488 0.829339 9.93062 0.868928 9.9992C0.908517 10.0678 0.961296 10.1279 1.02422 10.1759C1.08714 10.224 1.15896 10.2592 1.23554 10.2793L2.95682 10.7411C2.995 10.7523 3.02718 10.7781 3.0463 10.813C3.06543 10.8479 3.06991 10.8889 3.05878 10.9271L2.60297 12.7023C2.5625 12.855 2.5839 13.0175 2.6625 13.1545C2.7411 13.2915 2.87057 13.3919 3.02279 13.434C3.07444 13.4423 3.12708 13.4423 3.17873 13.434C3.31413 13.4387 3.44711 13.3973 3.55602 13.3167C3.66492 13.2361 3.74333 13.1211 3.77848 12.9902L4.37823 10.8311C4.38111 10.8076 4.38931 10.785 4.40222 10.7651L5.00197 10.4293C5.03654 10.4111 5.07668 10.4066 5.11444 10.4165C5.1522 10.4265 5.18483 10.4503 5.20588 10.4832L5.69768 11.3049C5.74971 11.3927 5.82328 11.4658 5.91144 11.5172C5.9996 11.5686 6.09942 11.5967 6.20147 11.5988H7.25103C7.29079 11.5988 7.32893 11.6146 7.35705 11.6427C7.38517 11.6708 7.40096 11.709 7.40096 11.7487V12.4864C7.39972 12.5261 7.3849 12.5642 7.35898 12.5944L5.77564 14.1717C5.66271 14.2847 5.59926 14.4378 5.59926 14.5976C5.59926 14.7573 5.66271 14.9104 5.77564 15.0234C5.88858 15.1363 6.04175 15.1998 6.20147 15.1998C6.36118 15.1998 6.51435 15.1363 6.62729 15.0234L7.89276 13.7519C7.90675 13.7374 7.92351 13.7258 7.94206 13.718C7.96061 13.7101 7.98056 13.706 8.00071 13.706C8.02087 13.706 8.04082 13.7101 8.05937 13.718C8.07791 13.7258 8.09468 13.7374 8.10867 13.7519L9.37414 15.0234C9.4299 15.0796 9.49623 15.1242 9.56931 15.1547C9.6424 15.1851 9.72079 15.2008 9.79996 15.2008C9.87914 15.2008 9.95753 15.1851 10.0306 15.1547C10.1037 15.1242 10.17 15.0796 10.2258 15.0234C10.282 14.9676 10.3266 14.9013 10.3571 14.8282C10.3875 14.7551 10.4032 14.6767 10.4032 14.5976C10.4032 14.5184 10.3875 14.44 10.3571 14.3669C10.3266 14.2938 10.282 14.2275 10.2258 14.1717L8.64245 12.5944C8.61653 12.5642 8.60171 12.5261 8.60046 12.4864V11.7487C8.60046 11.709 8.61626 11.6708 8.64438 11.6427C8.6725 11.6146 8.71064 11.5988 8.7504 11.5988H9.79996C9.90405 11.5988 10.0063 11.5717 10.0968 11.5201C10.1872 11.4686 10.2627 11.3944 10.3157 11.3049L10.8075 10.4832C10.8286 10.4503 10.8612 10.4265 10.899 10.4165C10.9367 10.4066 10.9769 10.4111 11.0115 10.4293L11.6112 10.7651C11.6458 10.7848 11.6715 10.817 11.6832 10.8551L12.2829 13.0142C12.3181 13.145 12.3965 13.2601 12.5054 13.3407C12.6143 13.4213 12.7473 13.4627 12.8827 13.458C12.9343 13.4663 12.987 13.4663 13.0386 13.458C13.1908 13.4159 13.3203 13.3155 13.3989 13.1785C13.4775 13.0415 13.4989 12.879 13.4584 12.7263L12.9307 10.999C12.9195 10.9609 12.924 10.9198 12.9431 10.885C12.9622 10.8501 12.9944 10.8242 13.0326 10.8131L14.7539 10.3513C14.8354 10.3335 14.9124 10.2989 14.9798 10.2497C15.0472 10.2005 15.1036 10.1377 15.1454 10.0655C15.1871 9.99323 15.2134 9.91305 15.2224 9.83008C15.2314 9.7471 15.223 9.66316 15.1977 9.58362ZM10.8735 7.9223C10.8852 7.94663 10.8913 7.97328 10.8913 8.00027C10.8913 8.02726 10.8852 8.05391 10.8735 8.07824L10.5556 8.60002C10.5356 8.63406 10.503 8.65883 10.4648 8.66893C10.4266 8.67904 10.386 8.67367 10.3517 8.654L9.44011 8.13222C9.41781 8.11817 9.39944 8.0987 9.3867 8.07563C9.37396 8.05255 9.36728 8.02663 9.36728 8.00027C9.36728 7.97391 9.37396 7.94799 9.3867 7.92491C9.39944 7.90184 9.41781 7.88237 9.44011 7.86832L10.3517 7.34654C10.386 7.32687 10.4266 7.3215 10.4648 7.33161C10.503 7.34171 10.5356 7.36648 10.5556 7.40052L10.8735 7.9223ZM9.37414 5.60126C9.40031 5.6017 9.42599 5.60847 9.44897 5.62101C9.47195 5.63354 9.49155 5.65146 9.50609 5.67323L9.76398 6.11105C9.78695 6.13539 9.79981 6.16754 9.79996 6.20101C9.79119 6.24032 9.76755 6.2747 9.73399 6.29697L8.84036 6.80676C8.81803 6.82122 8.792 6.82891 8.76539 6.82891C8.73879 6.82891 8.71276 6.82122 8.69043 6.80676C8.66803 6.79706 8.64822 6.78224 8.6326 6.76349C8.61697 6.74474 8.60596 6.72259 8.60046 6.69881V5.7512C8.60046 5.71143 8.61626 5.67329 8.64438 5.64517C8.6725 5.61705 8.71064 5.60126 8.7504 5.60126H9.37414ZM6.49534 5.67323C6.50988 5.65146 6.52948 5.63354 6.55246 5.62101C6.57544 5.60847 6.60112 5.6017 6.62729 5.60126H7.22704C7.2668 5.60126 7.30494 5.61705 7.33306 5.64517C7.36118 5.67329 7.37697 5.71143 7.37697 5.7512V6.69881C7.37707 6.72508 7.37052 6.75095 7.35793 6.77402C7.34535 6.79709 7.32715 6.8166 7.305 6.83075C7.28267 6.84521 7.25664 6.8529 7.23004 6.8529C7.20343 6.8529 7.1774 6.84521 7.15507 6.83075L6.28543 6.32096C6.26269 6.30937 6.24313 6.29237 6.22849 6.27145C6.21385 6.25054 6.20457 6.22635 6.20147 6.20101C6.19295 6.18322 6.18853 6.16375 6.18853 6.14403C6.18853 6.12431 6.19295 6.10485 6.20147 6.08706L6.49534 5.67323ZM5.14591 8.07224C5.13421 8.04791 5.12814 8.02127 5.12814 7.99427C5.12814 7.96728 5.13421 7.94063 5.14591 7.9163L5.46377 7.40052C5.4838 7.36648 5.51645 7.34171 5.55463 7.33161C5.59281 7.3215 5.63344 7.32687 5.66769 7.34654L6.57931 7.86832C6.60161 7.88237 6.61999 7.90184 6.63272 7.92491C6.64546 7.94799 6.65214 7.97391 6.65214 8.00027C6.65214 8.02663 6.64546 8.05255 6.63272 8.07563C6.61999 8.0987 6.60161 8.11817 6.57931 8.13222L5.66769 8.654C5.63344 8.67367 5.59281 8.67904 5.55463 8.66893C5.51645 8.65883 5.4838 8.63406 5.46377 8.60002L5.14591 8.07224ZM6.62729 10.3993C6.60112 10.3988 6.57544 10.3921 6.55246 10.3795C6.52948 10.367 6.50988 10.3491 6.49534 10.3273L6.23745 9.88949C6.21448 9.86515 6.20162 9.83299 6.20147 9.79953C6.21024 9.76022 6.23388 9.72584 6.26744 9.70357L7.17906 9.19978C7.20139 9.18532 7.22742 9.17762 7.25403 9.17762C7.28063 9.17762 7.30666 9.18532 7.32899 9.19978C7.35114 9.21392 7.36934 9.23344 7.38193 9.25651C7.39451 9.27957 7.40106 9.30545 7.40096 9.33172V10.2793C7.40096 10.3191 7.38517 10.3572 7.35705 10.3854C7.32893 10.4135 7.29079 10.4293 7.25103 10.4293L6.62729 10.3993ZM9.50609 10.3273C9.49155 10.3491 9.47195 10.367 9.44897 10.3795C9.42599 10.3921 9.40031 10.3988 9.37414 10.3993H8.77439C8.73462 10.3993 8.69649 10.3835 8.66837 10.3554C8.64025 10.3272 8.62445 10.2891 8.62445 10.2493V9.30173C8.62436 9.27546 8.63091 9.24959 8.64349 9.22652C8.65608 9.20345 8.67428 9.18394 8.69642 9.16979C8.71875 9.15533 8.74479 9.14764 8.77139 9.14764C8.79799 9.14764 8.82403 9.15533 8.84636 9.16979L9.73999 9.67958C9.75828 9.69385 9.77315 9.71203 9.78353 9.73278C9.7939 9.75353 9.79952 9.77633 9.79996 9.79953C9.80848 9.81732 9.8129 9.83679 9.8129 9.85651C9.8129 9.87623 9.80848 9.89569 9.79996 9.91348L9.50609 10.3273Z"
              fill="url(#paint0_linear_12131_64842)"
            />
          </g>
          <defs>
            <linearGradient
              id="paint0_linear_12131_64842"
              x1="8.00671"
              y1="0.800781"
              x2="8.00671"
              y2="15.2008"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#55E0FF" />
              <stop offset="1" stop-color="#5EA8FF" />
            </linearGradient>
            <clipPath id="clip0_12131_64842">
              <rect width="16" height="16" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ) : undefined}
      {props.align === "end" && props.selectedOdd === "total" ? (
        <svg
          x={200}
          y={y + 12}
          width="49"
          height="15"
          viewBox="0 0 49 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_12203_604974)">
            <path
              d="M6.01692 13.1359C6.17013 13.1355 6.32151 13.1027 6.46111 13.0395C6.6007 12.9763 6.72534 12.8842 6.82682 12.7693L11.2538 7.73346C11.3602 7.60338 11.412 7.43704 11.3982 7.26949C11.3845 7.10194 11.3063 6.94628 11.1802 6.83531C11.054 6.72433 10.8898 6.66672 10.7221 6.6746C10.5544 6.68249 10.3962 6.75525 10.281 6.87757L6.86147 10.7683C6.84635 10.7853 6.82641 10.7974 6.80429 10.8029C6.78217 10.8083 6.75891 10.8069 6.73759 10.7989C6.71628 10.7908 6.69791 10.7765 6.68492 10.7577C6.67193 10.739 6.66493 10.7168 6.66484 10.6939L6.66484 2.98461C6.66484 2.81257 6.59658 2.64758 6.47507 2.52593C6.35356 2.40428 6.18876 2.33594 6.01692 2.33594C5.84508 2.33594 5.68028 2.40428 5.55877 2.52593C5.43727 2.64758 5.369 2.81257 5.369 2.98461L5.369 10.6926C5.36878 10.7154 5.36168 10.7376 5.34864 10.7563C5.33559 10.775 5.3172 10.7893 5.29589 10.7973C5.27457 10.8054 5.25133 10.8068 5.22921 10.8013C5.20709 10.7959 5.18712 10.7839 5.17193 10.7669L1.75235 6.87622C1.69745 6.80817 1.62938 6.75195 1.55222 6.71092C1.47507 6.66989 1.39042 6.64491 1.30337 6.63748C1.21632 6.63005 1.12867 6.64032 1.04568 6.66767C0.962701 6.69502 0.886101 6.7389 0.82049 6.79665C0.754878 6.85441 0.701611 6.92485 0.663891 7.00374C0.626171 7.08264 0.604777 7.16835 0.600997 7.25574C0.597217 7.34312 0.611128 7.43037 0.641892 7.51224C0.672658 7.5941 0.719643 7.66889 0.78002 7.73211L5.20702 12.7688C5.30843 12.8838 5.43304 12.976 5.57265 13.0393C5.71225 13.1026 5.86367 13.1355 6.01692 13.1359Z"
              fill="#FF5C00"
            />
          </g>
          <path
            d="M20.7307 3.20508V9.11133C20.7229 9.99805 20.4788 10.6777 19.9983 11.1504C19.5178 11.6191 18.8284 11.8535 17.9299 11.8535C17.0159 11.8535 16.3166 11.6152 15.8323 11.1387C15.3518 10.6621 15.1116 9.97266 15.1116 9.07031V3.20508H16.8459V9.07617C16.8459 9.56055 16.9241 9.90625 17.0803 10.1133C17.2366 10.3203 17.5198 10.4238 17.9299 10.4238C18.3401 10.4238 18.6213 10.3223 18.7737 10.1191C18.926 9.91211 19.0041 9.57422 19.008 9.10547V3.20508H20.7307ZM28.1222 11.7363H26.3995L23.88 6.14062V11.7363H22.1573V3.20508H23.88L26.4054 8.80664V3.20508H28.1222V11.7363ZM29.625 11.7363V3.20508H31.8809C32.877 3.20508 33.6699 3.52148 34.2598 4.1543C34.8535 4.78711 35.1562 5.6543 35.168 6.75586V8.13867C35.168 9.25977 34.8711 10.1406 34.2773 10.7812C33.6875 11.418 32.873 11.7363 31.834 11.7363H29.625ZM31.3477 4.64062V10.3066H31.8633C32.4375 10.3066 32.8418 10.1562 33.0762 9.85547C33.3105 9.55078 33.4336 9.02734 33.4453 8.28516V6.80273C33.4453 6.00586 33.334 5.45117 33.1113 5.13867C32.8887 4.82227 32.5098 4.65625 31.9746 4.64062H31.3477ZM40.9364 8.04492H38.2528V10.3066H41.4286V11.7363H36.5302V3.20508H41.4169V4.64062H38.2528V6.65625H40.9364V8.04492ZM45.1287 8.61914H44.2732V11.7363H42.5505V3.20508H45.2986C46.1619 3.20508 46.8279 3.42969 47.2966 3.87891C47.7693 4.32422 48.0056 4.95898 48.0056 5.7832C48.0056 6.91602 47.5935 7.70898 46.7693 8.16211L48.2634 11.6543V11.7363H46.4119L45.1287 8.61914ZM44.2732 7.18359H45.2517C45.5955 7.18359 45.8533 7.07031 46.0252 6.84375C46.197 6.61328 46.283 6.30664 46.283 5.92383C46.283 5.06836 45.949 4.64062 45.281 4.64062H44.2732V7.18359Z"
            fill={props.dark ? "white" : "black"}
          />
          <defs>
            <clipPath id="clip0_12203_604974">
              <rect
                width="12"
                height="12"
                fill="white"
                transform="translate(0 1.73633)"
              />
            </clipPath>
          </defs>
        </svg>
      ) : props.align === "start" && props.selectedOdd === "total" ? (
        <svg
          x={24}
          y={y + 12}
          width="41"
          height="15"
          viewBox="0 0 41 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_12203_604982)">
            <path
              d="M6.01692 2.33672C6.17013 2.33712 6.32151 2.37 6.46111 2.4332C6.6007 2.4964 6.72534 2.58849 6.82682 2.7034L11.2538 7.7392C11.3602 7.86928 11.412 8.03562 11.3982 8.20317C11.3845 8.37071 11.3063 8.52637 11.1802 8.63735C11.054 8.74832 10.8898 8.80594 10.7221 8.79805C10.5544 8.79017 10.3962 8.7174 10.281 8.59509L6.86147 4.70438C6.84635 4.68732 6.82641 4.67526 6.80429 4.66979C6.78217 4.66433 6.75891 4.66571 6.73759 4.67377C6.71628 4.68183 6.69791 4.69618 6.68492 4.71492C6.67193 4.73365 6.66493 4.7559 6.66484 4.77871L6.66484 12.488C6.66484 12.6601 6.59658 12.8251 6.47507 12.9467C6.35356 13.0684 6.18876 13.1367 6.01692 13.1367C5.84508 13.1367 5.68028 13.0684 5.55877 12.9467C5.43727 12.8251 5.369 12.6601 5.369 12.488L5.369 4.78006C5.36878 4.75726 5.36168 4.73506 5.34864 4.71637C5.33559 4.69768 5.3172 4.68338 5.29589 4.67534C5.27457 4.6673 5.25133 4.6659 5.22921 4.67133C5.20709 4.67675 5.18712 4.68875 5.17193 4.70574L1.75235 8.59644C1.69745 8.66448 1.62938 8.72071 1.55222 8.76174C1.47507 8.80276 1.39042 8.82774 1.30337 8.83518C1.21632 8.84261 1.12867 8.83234 1.04568 8.80499C0.962701 8.77763 0.886101 8.73376 0.82049 8.676C0.754878 8.61825 0.701611 8.54781 0.663891 8.46891C0.626171 8.39002 0.604777 8.3043 0.600997 8.21692C0.597217 8.12953 0.611128 8.04228 0.641892 7.96042C0.672658 7.87855 0.719643 7.80377 0.78002 7.74055L5.20702 2.70385C5.30843 2.58881 5.43304 2.49661 5.57265 2.43333C5.71225 2.37004 5.86367 2.33712 6.01692 2.33672Z"
              fill="#99D61F"
            />
          </g>
          <path
            d="M20.7165 8.23828C20.7165 9.38281 20.445 10.2715 19.902 10.9043C19.363 11.5371 18.613 11.8535 17.652 11.8535C16.695 11.8535 15.943 11.541 15.3962 10.916C14.8493 10.2871 14.572 9.4082 14.5641 8.2793V6.82031C14.5641 5.64844 14.8356 4.73438 15.3786 4.07812C15.9216 3.41797 16.6755 3.08789 17.6403 3.08789C18.5895 3.08789 19.3376 3.41211 19.8845 4.06055C20.4313 4.70508 20.7087 5.61133 20.7165 6.7793V8.23828ZM18.988 6.80859C18.988 6.03906 18.8786 5.4668 18.6598 5.0918C18.4411 4.7168 18.1012 4.5293 17.6403 4.5293C17.1833 4.5293 16.8454 4.71094 16.6266 5.07422C16.4079 5.43359 16.2946 5.98242 16.2868 6.7207V8.23828C16.2868 8.98438 16.3981 9.53516 16.6208 9.89062C16.8434 10.2422 17.1872 10.418 17.652 10.418C18.1012 10.418 18.4352 10.2461 18.654 9.90234C18.8727 9.55469 18.9841 9.01758 18.988 8.29102V6.80859ZM24.7212 9.39258L26.1568 3.20508H28.0787L25.6177 11.7363H23.8248L21.3814 3.20508H23.2916L24.7212 9.39258ZM33.4077 8.04492H30.7241V10.3066H33.8998V11.7363H29.0014V3.20508H33.8881V4.64062H30.7241V6.65625H33.4077V8.04492ZM37.5999 8.61914H36.7445V11.7363H35.0218V3.20508H37.7698C38.6331 3.20508 39.2991 3.42969 39.7679 3.87891C40.2405 4.32422 40.4769 4.95898 40.4769 5.7832C40.4769 6.91602 40.0648 7.70898 39.2405 8.16211L40.7347 11.6543V11.7363H38.8831L37.5999 8.61914ZM36.7445 7.18359H37.723C38.0667 7.18359 38.3245 7.07031 38.4964 6.84375C38.6683 6.61328 38.7542 6.30664 38.7542 5.92383C38.7542 5.06836 38.4202 4.64062 37.7523 4.64062H36.7445V7.18359Z"
            fill={props.dark ? "white" : "black"}
          />
          <defs>
            <clipPath id="clip0_12203_604982">
              <rect
                width="12"
                height="12"
                fill="white"
                transform="translate(0 1.73633)"
              />
            </clipPath>
          </defs>
        </svg>
      ) : undefined}
      {props.selectedOdd !== "total" && (
        <rect
          x={x - (props.align === "start" ? 38 : -24)}
          y={y + 18}
          rx="3"
          ry="3"
          width="12"
          height="6"
          fill={props.legendColor}
          stroke={props.dark ? "white" : "none"}
          strokeWidth={1.5}
        />
      )}
      <foreignObject x={x - 50} y={y - 30} width="100" height="100">
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            marginTop: "-0.2rem",
            gap: props.selectedOdd === "total" ? "1.5rem" : "0.25rem",

            color: props.dark ? "var(--surface-700)" : "var(--secondary-300)",
          }}
        >
          <span
            className={`text-headline-bold ${
              props.dark ? "text-white" : "text-secondary-300"
            }`}
          >
            {props.value.toLocaleString()}%
          </span>
          {props.selectedOdd !== "total" && (
            <span
              className={`text-label-lg-bold ${
                props.dark ? "text-white" : "text-secondary-300"
              }`}
            >
              {props.team}
            </span>
          )}
          <span
            className={`text-label-lg ${
              props.dark ? "text-white" : "text-secondary-300"
            }`}
          >
            {props.picks || 0} Pick{props.picks === 1 ? "" : "s"}
          </span>
        </div>
      </foreignObject>
    </>
  );
};

function calculateBreakEven(value: number): number {
  let decimalOdds: number = 0;
  if (value < 0) {
    decimalOdds = 1 - 100 / value;
  } else {
    decimalOdds = value / 100 + 1;
  }
  return 1 / decimalOdds;
}

interface GamePerformanceChartProps {
  awayTeam: TeamBasic & TeamColor;
  homeTeam: TeamBasic & TeamColor;
  selectedOdd: OddVariant;
  awayOdd?: Odd2;
  homeOdd?: Odd2;
  awayTotalPicks: number;
  homeTotalPicks: number;
  dark?: boolean;
}

interface GameOddsChartTabberProps {
  game?: ScoreboardGame;
  dark?: boolean;
  defaultOdd: OddVariant;
}

function GameOddsChartTabber(props: GameOddsChartTabberProps) {
  if (props.game) {
    return (
      <div className="flex justify-center w-full">
        <Tabs
          label="Choose an Odd"
          variant="pill-sm"
          buttonWidth={89}
          dark={props.dark}
          defaultValue={props.defaultOdd}
          items={[
            {
              label: "Moneyline",
              value: "moneyline",
              content: (
                <GamePerformanceChart
                  awayTeam={props.game.awayTeam}
                  homeTeam={props.game.homeTeam}
                  selectedOdd="moneyline"
                  awayOdd={buildOdd(LineType.Moneyline, GameAlignmentType.Away, props.game.odds)}
                  homeOdd={buildOdd(LineType.Moneyline, GameAlignmentType.Home, props.game.odds)}
                  awayTotalPicks={props.game.awayMoneylinePicks}
                  homeTotalPicks={props.game.homeMoneylinePicks}
                  dark={props.dark}
                />
              ),
            },
            {
              label: "Spread",
              value: "spread",
              content: (
                <GamePerformanceChart
                  awayTeam={props.game.awayTeam}
                  homeTeam={props.game.homeTeam}
                  selectedOdd="spread"
                  awayOdd={buildOdd(LineType.Spread, GameAlignmentType.Away,  props.game.odds)}
                  homeOdd={buildOdd(LineType.Spread, GameAlignmentType.Home, props.game.odds)}
                  awayTotalPicks={props.game.awaySpreadPicks}
                  homeTotalPicks={props.game.homeSpreadPicks}
                  dark={props.dark}
                />
              ),
            },
            {
              label: "Total",
              value: "total",
              content: (
                <GamePerformanceChart
                  awayTeam={props.game.awayTeam}
                  homeTeam={props.game.homeTeam}
                  selectedOdd="total"
                  awayOdd={buildOdd(LineType.Total, GameAlignmentType.Away, props.game.odds)}
                  homeOdd={buildOdd(LineType.Spread, GameAlignmentType.Home, props.game.odds)}
                  awayTotalPicks={props.game.overPicks}
                  homeTotalPicks={props.game.underPicks}
                  dark={props.dark}
                />
              ),
            },
          ]}
        />
      </div>
    );
  }
}

function GamePerformanceChart(props: GamePerformanceChartProps) {
  const color: string = props.dark
    ? "var(--surface-700)"
    : "var(--secondary-300)";
  const awayPct: number = calculatePercentage(
    props.awayTotalPicks,
    props.awayTotalPicks + props.homeTotalPicks
  );
  const homePct: number = calculatePercentage(
    props.homeTotalPicks,
    props.awayTotalPicks + props.homeTotalPicks
  );
  const zeroValues: number | undefined =
    awayPct === 0 && homePct === 0 ? 50 : undefined;
  const data = [
    {
      team:
        props.selectedOdd === "total"
          ? "OVER"
          : `${props.awayTeam.abbreviation} ${formatOddVariantShort(
              props.selectedOdd
            )}`,
      picks: props.awayTotalPicks,
      value: zeroValues ? 50 : awayPct,
      alignment: "away",
      color:
        props.selectedOdd === "total"
          ? "var(--primary-400)"
          : `#${props.awayTeam.primaryColor}`,
      legendColor:
        props.selectedOdd === "total"
          ? "var(--primary-400)"
          : `#${props.awayTeam.primaryColor}`,
      odd: props.awayOdd,
      temperature:
        awayPct > homePct ? "hot" : awayPct < homePct ? "cold" : "none",
      selectedOdd: props.selectedOdd,
      dark: props.dark,
    },
    {
      team:
        props.selectedOdd === "total"
          ? "UNDER"
          : `${props.homeTeam.abbreviation} ${formatOddVariantShort(
              props.selectedOdd
            )}`,
      picks: props.homeTotalPicks,
      value: zeroValues ? 50 : homePct,
      alignment: "home",
      color:
        props.selectedOdd === "total"
          ? "var(--hot)"
          : `#${props.homeTeam.primaryColor}`,
      legendColor:
        props.selectedOdd === "total"
          ? "var(--hot)"
          : `#${props.homeTeam.primaryColor}`,
      odd: props.homeOdd,
      temperature:
        homePct > awayPct ? "hot" : homePct < awayPct ? "cold" : "none",
      selectedOdd: props.selectedOdd,
      dark: props.dark,
    },
  ];
  const teamWithHigherValue =
    awayPct > homePct ? props.awayTeam : props.homeTeam;
  return (
    <>
      {/* <div className="flex justify-center items-center gap-2 px-2 pt-3 pb-1">
        <div className="text-label-sm-medium text-secondary-500 uppercase">
          {props.label}
        </div>
      </div> */}
      <div className="flex items-center w-full">
        <ResponsiveContainer width="100%" height={120}>
          <PieChart height={100} width={100}>
            <Pie
              animationDuration={150}
              animationEasing="ease-in-out"
              data={data}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={31}
              outerRadius={50}
              textAnchor="middle"
              dominantBaseline={"central"}
              paddingAngle={3}
              label={(labelProps) =>
                renderCustomLabel({
                  align: labelProps.alignment === "home" ? "end" : "start",
                  superbowl: props.dark,
                  cy: "50%",
                  cx: "50%",
                  ...labelProps,
                })
              }
              labelLine={false}
              startAngle={-270}
              endAngle={90}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke={props.dark ? "#FFFFFF" : undefined}
                  strokeWidth={props.dark ? 3 : undefined}
                />
              ))}
            </Pie>
            {props.selectedOdd === "total" && awayPct > homePct ? (
              <svg
                x={116}
                y={54}
                width="37"
                height="14"
                viewBox="0 0 37 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_11741_705595)">
                  <path
                    d="M6.01692 1.60039C6.17013 1.60079 6.32151 1.63367 6.46111 1.69687C6.6007 1.76007 6.72534 1.85216 6.82682 1.96707L11.2538 7.00287C11.3602 7.13295 11.412 7.29929 11.3982 7.46684C11.3845 7.63438 11.3063 7.79005 11.1802 7.90102C11.054 8.01199 10.8898 8.06961 10.7221 8.06173C10.5544 8.05384 10.3962 7.98108 10.281 7.85876L6.86147 3.96806C6.84635 3.95099 6.82641 3.93893 6.80429 3.93346C6.78217 3.928 6.75891 3.92938 6.73759 3.93744C6.71628 3.9455 6.69791 3.95985 6.68492 3.97859C6.67193 3.99733 6.66493 4.01957 6.66484 4.04238L6.66484 11.7517C6.66484 11.9238 6.59658 12.0887 6.47507 12.2104C6.35356 12.332 6.18876 12.4004 6.01692 12.4004C5.84508 12.4004 5.68028 12.332 5.55877 12.2104C5.43727 12.0887 5.369 11.9238 5.369 11.7517L5.369 4.04374C5.36878 4.02093 5.36168 3.99873 5.34864 3.98004C5.33559 3.96136 5.3172 3.94705 5.29589 3.93901C5.27457 3.93098 5.25133 3.92958 5.22921 3.935C5.20709 3.94042 5.18712 3.95242 5.17193 3.96941L1.75235 7.86011C1.69745 7.92815 1.62938 7.98438 1.55222 8.02541C1.47507 8.06644 1.39042 8.09142 1.30337 8.09885C1.21632 8.10628 1.12867 8.09601 1.04568 8.06866C0.962701 8.0413 0.886101 7.99743 0.82049 7.93968C0.754878 7.88192 0.701611 7.81148 0.663891 7.73259C0.626171 7.65369 0.604777 7.56797 0.600997 7.48059C0.597217 7.3932 0.611128 7.30596 0.641892 7.22409C0.672658 7.14223 0.719643 7.06744 0.78002 7.00422L5.20702 1.96752C5.30843 1.85248 5.43304 1.76028 5.57265 1.697C5.71225 1.63371 5.86367 1.60079 6.01692 1.60039Z"
                    fill="#99D61F"
                  />
                </g>
                <path
                  d="M22.6914 7.50195C22.6914 8.64648 22.4199 9.53516 21.877 10.168C21.3379 10.8008 20.5879 11.1172 19.627 11.1172C18.6699 11.1172 17.918 10.8047 17.3711 10.1797C16.8242 9.55078 16.5469 8.67188 16.5391 7.54297V6.08398C16.5391 4.91211 16.8105 3.99805 17.3535 3.3418C17.8965 2.68164 18.6504 2.35156 19.6152 2.35156C20.5645 2.35156 21.3125 2.67578 21.8594 3.32422C22.4062 3.96875 22.6836 4.875 22.6914 6.04297V7.50195ZM20.9629 6.07227C20.9629 5.30273 20.8535 4.73047 20.6348 4.35547C20.416 3.98047 20.0762 3.79297 19.6152 3.79297C19.1582 3.79297 18.8203 3.97461 18.6016 4.33789C18.3828 4.69727 18.2695 5.24609 18.2617 5.98438V7.50195C18.2617 8.24805 18.373 8.79883 18.5957 9.1543C18.8184 9.50586 19.1621 9.68164 19.627 9.68164C20.0762 9.68164 20.4102 9.50977 20.6289 9.16602C20.8477 8.81836 20.959 8.28125 20.9629 7.55469V6.07227ZM26.6962 8.65625L28.1317 2.46875H30.0536L27.5927 11H25.7997L23.3563 2.46875H25.2665L26.6962 8.65625ZM33.5603 7.88281H32.7048V11H30.9822V2.46875H33.7302C34.5935 2.46875 35.2595 2.69336 35.7283 3.14258C36.2009 3.58789 36.4373 4.22266 36.4373 5.04688C36.4373 6.17969 36.0252 6.97266 35.2009 7.42578L36.6951 10.918V11H34.8435L33.5603 7.88281ZM32.7048 6.44727H33.6834C34.0271 6.44727 34.2849 6.33398 34.4568 6.10742C34.6287 5.87695 34.7146 5.57031 34.7146 5.1875C34.7146 4.33203 34.3806 3.9043 33.7127 3.9043H32.7048V6.44727Z"
                  fill="black"
                />
                <defs>
                  <clipPath id="clip0_11741_705595">
                    <rect
                      width="12"
                      height="12"
                      fill="none"
                      transform="translate(0 1)"
                    />
                  </clipPath>
                </defs>
              </svg>
            ) : props.selectedOdd === "total" && homePct > awayPct ? (
              <svg
                x={116}
                y={54}
                width="37"
                height="14"
                viewBox="0 0 37 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_11741_705611)">
                  <path
                    d="M6.01692 12.3996C6.17013 12.3992 6.32151 12.3663 6.46111 12.3031C6.6007 12.2399 6.72534 12.1478 6.82682 12.0329L11.2538 6.99713C11.3602 6.86705 11.412 6.70071 11.3982 6.53316C11.3845 6.36562 11.3063 6.20995 11.1802 6.09898C11.054 5.98801 10.8898 5.93039 10.7221 5.93827C10.5544 5.94616 10.3962 6.01892 10.281 6.14124L6.86147 10.0319C6.84635 10.049 6.82641 10.0611 6.80429 10.0665C6.78217 10.072 6.75891 10.0706 6.73759 10.0626C6.71628 10.0545 6.69791 10.0402 6.68492 10.0214C6.67193 10.0027 6.66493 9.98043 6.66484 9.95762L6.66484 2.24829C6.66484 2.07625 6.59658 1.91125 6.47507 1.7896C6.35356 1.66795 6.18876 1.59961 6.01692 1.59961C5.84508 1.59961 5.68028 1.66795 5.55877 1.7896C5.43727 1.91125 5.369 2.07625 5.369 2.24829L5.369 9.95626C5.36878 9.97907 5.36168 10.0013 5.34864 10.02C5.33559 10.0386 5.3172 10.0529 5.29589 10.061C5.27457 10.069 5.25133 10.0704 5.22921 10.065C5.20709 10.0596 5.18712 10.0476 5.17193 10.0306L1.75235 6.13989C1.69745 6.07185 1.62938 6.01562 1.55222 5.97459C1.47507 5.93356 1.39042 5.90858 1.30337 5.90115C1.21632 5.89372 1.12867 5.90399 1.04568 5.93134C0.962701 5.9587 0.886101 6.00257 0.82049 6.06032C0.754878 6.11808 0.701611 6.18852 0.663891 6.26741C0.626171 6.34631 0.604777 6.43203 0.600997 6.51941C0.597217 6.6068 0.611128 6.69404 0.641892 6.77591C0.672658 6.85777 0.719643 6.93256 0.78002 6.99578L5.20702 12.0325C5.30843 12.1475 5.43304 12.2397 5.57265 12.303C5.71225 12.3663 5.86367 12.3992 6.01692 12.3996Z"
                    fill="#FF5C00"
                  />
                </g>
                <path
                  d="M21.2344 2.46875V8.375C21.2266 9.26172 20.9824 9.94141 20.502 10.4141C20.0215 10.8828 19.332 11.1172 18.4336 11.1172C17.5195 11.1172 16.8203 10.8789 16.3359 10.4023C15.8555 9.92578 15.6152 9.23633 15.6152 8.33398V2.46875H17.3496V8.33984C17.3496 8.82422 17.4277 9.16992 17.584 9.37695C17.7402 9.58398 18.0234 9.6875 18.4336 9.6875C18.8438 9.6875 19.125 9.58594 19.2773 9.38281C19.4297 9.17578 19.5078 8.83789 19.5117 8.36914V2.46875H21.2344ZM28.6259 11H26.9032L24.3837 5.4043V11H22.661V2.46875H24.3837L26.9091 8.07031V2.46875H28.6259V11ZM30.1287 11V2.46875H32.3845C33.3806 2.46875 34.1736 2.78516 34.7634 3.41797C35.3572 4.05078 35.6599 4.91797 35.6716 6.01953V7.40234C35.6716 8.52344 35.3748 9.4043 34.781 10.0449C34.1912 10.6816 33.3767 11 32.3377 11H30.1287ZM31.8513 3.9043V9.57031H32.367C32.9412 9.57031 33.3455 9.41992 33.5798 9.11914C33.8142 8.81445 33.9373 8.29102 33.949 7.54883V6.06641C33.949 5.26953 33.8377 4.71484 33.615 4.40234C33.3923 4.08594 33.0134 3.91992 32.4783 3.9043H31.8513Z"
                  fill="black"
                />
                <defs>
                  <clipPath id="clip0_11741_705611">
                    <rect
                      width="12"
                      height="12"
                      fill="none"
                      transform="translate(0 1)"
                    />
                  </clipPath>
                </defs>
              </svg>
            ) : props.selectedOdd === "total" && zeroValues ? (
              <svg
                x={120}
                y={46}
                width="36"
                height="31"
                viewBox="0 0 36 31"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="36" height="31" fill="none" />
                <g clip-path="url(#clip0_12163_558402)">
                  <path
                    d="M6.01692 9.60039C6.17013 9.60079 6.32151 9.63367 6.46111 9.69687C6.6007 9.76007 6.72534 9.85216 6.82682 9.96707L11.2538 15.0029C11.3602 15.133 11.412 15.2993 11.3982 15.4668C11.3845 15.6344 11.3063 15.79 11.1802 15.901C11.054 16.012 10.8898 16.0696 10.7221 16.0617C10.5544 16.0538 10.3962 15.9811 10.281 15.8588L6.86147 11.9681C6.84635 11.951 6.82641 11.9389 6.80429 11.9335C6.78217 11.928 6.75891 11.9294 6.73759 11.9374C6.71628 11.9455 6.69791 11.9598 6.68492 11.9786C6.67193 11.9973 6.66493 12.0196 6.66484 12.0424L6.66484 19.7517C6.66484 19.9238 6.59658 20.0887 6.47507 20.2104C6.35356 20.332 6.18876 20.4004 6.01692 20.4004C5.84508 20.4004 5.68028 20.332 5.55877 20.2104C5.43727 20.0887 5.369 19.9238 5.369 19.7517L5.369 12.0437C5.36878 12.0209 5.36168 11.9987 5.34864 11.98C5.33559 11.9614 5.3172 11.9471 5.29589 11.939C5.27457 11.931 5.25133 11.9296 5.22921 11.935C5.20709 11.9404 5.18712 11.9524 5.17193 11.9694L1.75235 15.8601C1.69745 15.9282 1.62938 15.9844 1.55222 16.0254C1.47507 16.0664 1.39042 16.0914 1.30337 16.0988C1.21632 16.1063 1.12867 16.096 1.04568 16.0687C0.962701 16.0413 0.886101 15.9974 0.82049 15.9397C0.754878 15.8819 0.701611 15.8115 0.663891 15.7326C0.626171 15.6537 0.604777 15.568 0.600997 15.4806C0.597217 15.3932 0.611128 15.306 0.641892 15.2241C0.672658 15.1422 0.719643 15.0674 0.78002 15.0042L5.20702 9.96752C5.30843 9.85248 5.43304 9.76028 5.57265 9.697C5.71225 9.63371 5.86367 9.60079 6.01692 9.60039Z"
                    fill="#99D61F"
                  />
                </g>
                <g clip-path="url(#clip1_12163_558402)">
                  <path
                    d="M30.0169 20.3996C30.1701 20.3992 30.3215 20.3663 30.4611 20.3031C30.6007 20.2399 30.7253 20.1478 30.8268 20.0329L35.2538 14.9971C35.3602 14.867 35.412 14.7007 35.3982 14.5332C35.3845 14.3656 35.3063 14.21 35.1802 14.099C35.054 13.988 34.8898 13.9304 34.7221 13.9383C34.5544 13.9462 34.3962 14.0189 34.281 14.1412L30.8615 18.0319C30.8463 18.049 30.8264 18.0611 30.8043 18.0665C30.7822 18.072 30.7589 18.0706 30.7376 18.0626C30.7163 18.0545 30.6979 18.0402 30.6849 18.0214C30.6719 18.0027 30.6649 17.9804 30.6648 17.9576L30.6648 10.2483C30.6648 10.0762 30.5966 9.91125 30.4751 9.7896C30.3536 9.66795 30.1888 9.59961 30.0169 9.59961C29.8451 9.59961 29.6803 9.66795 29.5588 9.7896C29.4373 9.91125 29.369 10.0762 29.369 10.2483L29.369 17.9563C29.3688 17.9791 29.3617 18.0013 29.3486 18.02C29.3356 18.0386 29.3172 18.0529 29.2959 18.061C29.2746 18.069 29.2513 18.0704 29.2292 18.065C29.2071 18.0596 29.1871 18.0476 29.1719 18.0306L25.7523 14.1399C25.6975 14.0718 25.6294 14.0156 25.5522 13.9746C25.4751 13.9336 25.3904 13.9086 25.3034 13.9012C25.2163 13.8937 25.1287 13.904 25.0457 13.9313C24.9627 13.9587 24.8861 14.0026 24.8205 14.0603C24.7549 14.1181 24.7016 14.1885 24.6639 14.2674C24.6262 14.3463 24.6048 14.432 24.601 14.5194C24.5972 14.6068 24.6111 14.694 24.6419 14.7759C24.6727 14.8578 24.7196 14.9326 24.78 14.9958L29.207 20.0325C29.3084 20.1475 29.433 20.2397 29.5726 20.303C29.7123 20.3663 29.8637 20.3992 30.0169 20.3996Z"
                    fill="#FF5C00"
                  />
                </g>
                <path
                  d="M23.5 1L12.5 30.5"
                  stroke="#A3A8B1"
                  stroke-linecap="round"
                />
                <defs>
                  <clipPath id="clip0_12163_558402">
                    <rect
                      width="12"
                      height="12"
                      fill="white"
                      transform="translate(0 9)"
                    />
                  </clipPath>
                  <clipPath id="clip1_12163_558402">
                    <rect
                      width="12"
                      height="12"
                      fill="white"
                      transform="translate(24 9)"
                    />
                  </clipPath>
                </defs>
              </svg>
            ) : zeroValues ? (
              <Customized
                component={() => (
                  <SideBySideTeamLogo
                    awayTeamLogo={props.awayTeam.logoUrl}
                    homeTeamLogo={props.homeTeam.logoUrl}
                    cx={155}
                    cy={60}
                    width={45}
                    height={33}
                  />
                )}
              />
            ) : (
              <svg>
                <image
                  href={teamWithHigherValue.logoUrl}
                  x={"50%"}
                  y={"50%"}
                  height={"40px"}
                  width={"40px"}
                  transform={"translate(-20, -20)"}
                />
              </svg>
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

export default GameOddsChartTabber;
